import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Server, CheckCircle2, AlertCircle, Loader2, Cpu } from 'lucide-react';
import baseapi from '../../../services/baseapi';
import DBTypeIcon from '../../../components/DBTypeIcon';

interface AgentCheck {
  db_reachable?: boolean;
  pg_stat_statements?: boolean;
  pg_stat_activity?: boolean;
  metrics_ingestion?: boolean;
  [key: string]: boolean | undefined;
}

interface AgentHealthNode {
  agent_id: number;
  hostname: string;
  ip: string;
  host: string;
  port: number;
  db_type: string;
  role: string;
  version: string | null;
  db_uptime_sec: number | null;
  last_collection: string;
  collection_age_sec: number;
  daemon_status: string;
  sync_rate_sec: number;
  host_mem_used_pct: number | null;
  checks: AgentCheck;
}

interface HealthTabProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
}

const formatUptime = (sec: number | null | undefined) => {
  if (!sec && sec !== 0) return "-";
  if (sec === 0) return '0s';
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m ${sec % 60}s`;
};

const getRoleBadgeColor = (role: string) => {
  const r = (role || '').toLowerCase();
  if (r === 'primary' || r === 'master') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
  if (r === 'replica' || r === 'standby' || r === 'secondary') return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
  return 'bg-purple-500/10 text-purple-400 border-purple-500/30';
};

const getMemColorClass = (pct: number) => {
  if (pct > 85) return { text: 'text-red-400', bg: 'bg-red-500' };
  if (pct > 70) return { text: 'text-amber-400', bg: 'bg-amber-500' };
  return { text: 'text-emerald-400', bg: 'bg-emerald-500' };
};

export const HealthTab: React.FC<HealthTabProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<AgentHealthNode[]>([]);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    try {
      const res = await baseapi.get('/db_monitor_dashboard/agent_health', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      const dataArr = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setAgents(dataArr);
    } catch (err) {
      console.warn('HealthTab agent_health fetch error:', err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const healthyCount = agents.filter((a) => a.daemon_status === 'healthy' && a.checks?.db_reachable !== false).length;

  const renderChecks = (checks: AgentCheck = {}) => {
    const checkEntries = Object.entries(checks);
    if (checkEntries.length === 0) {
      return <span className="text-gray-500 italic text-[10px]">No checks reported</span>;
    }

    const formatCheckLabel = (key: string) => {
      if (key === 'db_reachable') return 'DB Reachable';
      if (key === 'pg_stat_statements') return 'pg_stat_stmts';
      if (key === 'pg_stat_activity') return 'pg_stat_activity';
      if (key === 'metrics_ingestion') return 'Ingestion';
      if (key === 'node_stats') return 'Node Stats';
      if (key === 'cluster_health') return 'Cluster Health';
      return key.replace(/_/g, ' ');
    };

    const formatCheckStatus = (key: string, val?: boolean) => {
      if (val === true) {
        const positiveText = key === 'db_reachable' ? 'Yes' : 'Active';
        return (
          <span className="text-emerald-400 font-bold flex items-center gap-0.5">
            <CheckCircle2 className="w-3 h-3 shrink-0" /> {positiveText}
          </span>
        );
      }
      const negativeText = key === 'db_reachable' ? 'No' : 'Off';
      return (
        <span className="text-red-400 font-bold flex items-center gap-0.5">
          <AlertCircle className="w-3 h-3 shrink-0" /> {negativeText}
        </span>
      );
    };

    return (
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        {checkEntries.map(([key, val]) => (
          <div
            key={key}
            className="bg-gray-950/40 border border-gray-800 rounded px-2 py-1 flex items-center justify-between"
          >
            <span className="text-gray-400 truncate pr-1">{formatCheckLabel(key)}</span>
            {formatCheckStatus(key, val)}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Header View */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-800 pb-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-wide">
            <div className="p-1.5 rounded bg-orange-500  text-white">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            
            Cluster Agent Health & Monitoring Daemons
          </h2>
        </div>

        {agents.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-gray-800/80 border border-gray-700/80 rounded-lg text-xs text-gray-300 flex items-center gap-1.5 font-mono">
              <Server className="w-3.5 h-3.5 text-blue-400" />
              <span>Agents:</span>
              <strong className="text-white font-bold">{agents.length}</strong>
            </span>
            <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-400 flex items-center gap-1.5 font-mono font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Healthy:</span>
              <span>{healthyCount} / {agents.length}</span>
            </span>
          </div>
        )}
      </div>

      {/* Agent Cards Grid */}
      {agents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent) => {
            const isHealthy = agent.daemon_status === 'healthy' && agent.checks?.db_reachable !== false;
            const memPct = agent.host_mem_used_pct;

            return (
              <div
                key={agent.agent_id}
                className={`bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90 border ${
                  isHealthy ? 'border-gray-700/60 hover:border-emerald-500/40' : 'border-red-500/40'
                } rounded-xl p-4 shadow-xl flex flex-col justify-between transition-all duration-200 group`}
              >
                <div>
                  {/* Card Top Banner */}
                  <div className="flex items-start justify-between gap-2 border-b border-gray-800 pb-3 mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="p-2 rounded-lg bg-gray-800 border border-gray-700 shrink-0">
                        <DBTypeIcon dbType={agent.db_type} className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-xs font-bold text-white truncate group-hover:text-orange-400 transition-colors" title={agent.hostname}>
                          {agent.hostname}
                        </h3>
                        <p className="text-[11px] font-mono text-gray-400 truncate">
                          {agent.ip || agent.host ? `${agent.ip || agent.host}:${agent.port || ''}` : agent.hostname}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${getRoleBadgeColor(agent.role)}`}>
                        {agent.role || 'Standalone'}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1 ${
                          isHealthy
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                            : 'bg-red-500/10 text-red-400 border-red-500/30'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isHealthy ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                        {agent.daemon_status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Metadata Specs */}
                  <div className="grid grid-cols-2 gap-2 text-[11px] font-mono mb-3 bg-gray-950/60 p-2.5 rounded-lg border border-gray-800/80">
                    <div>
                      <span className="text-gray-200 text-[10px] block uppercase font-semibold">Agent ID</span>
                      <span className="text-gray-200 font-bold">{agent.agent_id}</span>
                    </div>
                    <div>
                      <span className="text-gray-200 text-[10px] block uppercase font-semibold">Sync Rate</span>
                      <span className="text-gray-200">{agent.sync_rate_sec}s</span>
                    </div>
                    <div>
                      <span className="text-gray-200 text-[10px] block uppercase font-semibold">DB Uptime</span>
                      <span className="text-emerald-400 font-bold">{formatUptime(agent.db_uptime_sec)}</span>
                    </div>
                    <div>
                      <span className="text-gray-200 text-[10px] block uppercase font-semibold">Collect Age</span>
                      <span className={agent.collection_age_sec > 120 ? 'text-red-400 font-bold' : 'text-gray-200'}>
                        {agent.collection_age_sec != null ? `${Number(agent.collection_age_sec).toFixed(1)}s ago` : "-"}
                      </span>
                    </div>
                  </div>

                  {/* Memory Usage Bar with Green, Yellow, Red threshold styling */}
                  {memPct !== null && memPct !== undefined && (
                    (() => {
                      const memColor = getMemColorClass(memPct);
                      return (
                        <div className="mb-3">
                          <div className="flex justify-between items-center text-[10px] font-mono mb-1">
                            <span className="text-gray-400 flex items-center gap-1">
                              <Cpu className={`w-3 h-3 ${memColor.text}`} /> Host Memory Used
                            </span>
                            <span className={`${memColor.text} font-bold`}>{memPct.toFixed(1)}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden border border-gray-700/50">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${memColor.bg}`}
                              style={{ width: `${Math.min(Math.max(memPct, 0), 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Diagnostic Checks Section */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 flex items-center gap-1 mb-1">
                      <ShieldCheck className="w-3 h-3 text-blue-400" /> Diagnostic Checks
                    </span>

                    {renderChecks(agent.checks)}
                  </div>
                </div>

                {/* Footer: Version & Last Collect */}
                <div className="mt-3 pt-2.5 border-t border-gray-800 flex items-center justify-between text-[10px] text-gray-400 font-mono">
                  <span className="truncate max-w-[170px]" title={agent.version || 'Version -'}>
                    {agent.version ? agent.version.split(' ')[0] : 'v -'}
                  </span>
                  <span className="text-gray-500">
                    {agent.last_collection ? new Date(agent.last_collection).toLocaleTimeString() : "-"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-8 text-center shadow-xl">
          <AlertCircle className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-xs text-gray-400 italic">No agent health telemetry reported for this cluster.</p>
        </div>
      )}
    </div>
  );
};
